import { useEffect, useState, useCallback } from "react";
import { useProject } from "@/contexts/ProjectContext";
import api from "@/lib/api";
import { BDT, formatDate, todayDhaka } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const ENTRY_TYPES = ["Client Due", "Client Payment", "Team Salary", "Team Expense"];
const PAYMENT_METHODS = ["Cash", "Bank", "bKash", "Nagad", "Other"];

const emptyForm = () => ({
  type: "Client Due",
  amount: "",
  date: todayDhaka(),
  person: "",
  purpose: "",
  payment_method: "Cash",
  note: "",
});

function typeBadge(t) {
  const map = {
    "Client Due": "bg-rose-50 text-rose-700 border-rose-200",
    "Client Payment": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Team Salary": "bg-indigo-50 text-indigo-700 border-indigo-200",
    "Team Expense": "bg-amber-50 text-amber-700 border-amber-200",
  };
  return map[t] || "bg-slate-50 text-slate-700 border-slate-200";
}

export default function Accounts() {
  const { selected } = useProject();
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ type: "all", person: "", start_date: "", end_date: "" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const params = {};
      if (filters.type && filters.type !== "all") params.type = filters.type;
      if (filters.person) params.person = filters.person;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const [e, s] = await Promise.all([
        api.get(`/projects/${selected.id}/accounts`, { params }),
        api.get(`/projects/${selected.id}/accounts/summary`),
      ]);
      setEntries(e.data);
      setSummary(s.data);
    } finally {
      setLoading(false);
    }
  }, [selected, filters]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (entry) => {
    setEditing(entry);
    setForm({
      type: entry.type,
      amount: String(entry.amount),
      date: entry.date,
      person: entry.person,
      purpose: entry.purpose || "",
      payment_method: entry.payment_method,
      note: entry.note || "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount || 0) };
      if (editing) {
        await api.patch(`/projects/${selected.id}/accounts/${editing.id}`, payload);
        toast.success("Entry updated");
      } else {
        await api.post(`/projects/${selected.id}/accounts`, payload);
        toast.success("Entry added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this entry?")) return;
    await api.delete(`/projects/${selected.id}/accounts/${id}`);
    toast.success("Deleted");
    load();
  };

  if (!selected) return <div className="text-sm text-slate-500">Select a project first.</div>;

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="accounts-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Accounts</h1>
          <p className="text-sm text-slate-500 mt-1">Track client payments, dues and team expenses</p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-account-button">
          <Plus className="h-4 w-4 mr-2" /> Add Entry
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Client Due", value: summary.client_due, color: "text-rose-700" },
            { label: "Client Paid", value: summary.client_paid, color: "text-emerald-700" },
            { label: "Team Salary + Expense", value: summary.team_total, color: "text-indigo-700" },
            { label: "Net Balance", value: summary.net_balance, color: summary.net_balance < 0 ? "text-rose-700" : "text-slate-900" },
          ].map((c) => (
            <Card key={c.label} className="rounded-2xl border-slate-200 shadow-sm">
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-widest text-slate-500">{c.label}</div>
                <div className={`font-display text-2xl font-bold mt-1 tracking-tighter ${c.color}`}>{BDT(c.value)}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Card className="rounded-2xl border-slate-200 shadow-sm">
        <CardContent className="p-5 flex flex-wrap gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs">Type</Label>
            <Select value={filters.type} onValueChange={(v) => setFilters({ ...filters, type: v })}>
              <SelectTrigger className="w-44" data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {ENTRY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Person</Label>
            <Input value={filters.person} onChange={(e) => setFilters({ ...filters, person: e.target.value })} placeholder="Search by name" className="w-48" data-testid="filter-person" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">From</Label>
            <Input type="date" value={filters.start_date} onChange={(e) => setFilters({ ...filters, start_date: e.target.value })} className="w-40" data-testid="filter-start" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To</Label>
            <Input type="date" value={filters.end_date} onChange={(e) => setFilters({ ...filters, end_date: e.target.value })} className="w-40" data-testid="filter-end" />
          </div>
          <Button variant="outline" onClick={() => setFilters({ type: "all", person: "", start_date: "", end_date: "" })} data-testid="filter-clear">Clear</Button>
        </CardContent>
      </Card>

      <Card className="rounded-2xl border-slate-200 shadow-sm overflow-hidden">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Person</TableHead>
              <TableHead>Purpose</TableHead>
              <TableHead>Method</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">Loading...</TableCell></TableRow>
            ) : entries.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">No entries yet.</TableCell></TableRow>
            ) : entries.map((e) => (
              <TableRow key={e.id} data-testid={`account-row-${e.id}`}>
                <TableCell>{formatDate(e.date)}</TableCell>
                <TableCell><Badge variant="outline" className={`${typeBadge(e.type)} text-[10px]`}>{e.type}</Badge></TableCell>
                <TableCell className="font-medium text-slate-800">{e.person}</TableCell>
                <TableCell className="text-slate-600">{e.purpose}</TableCell>
                <TableCell>{e.payment_method}</TableCell>
                <TableCell className="text-right font-semibold">{BDT(e.amount)}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(e)} data-testid={`edit-account-${e.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(e.id)} data-testid={`delete-account-${e.id}`}><Trash2 className="h-3.5 w-3.5 text-rose-600" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Edit Entry" : "Add Account Entry"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="form-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{ENTRY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Amount (৳)</Label>
                <Input type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="form-amount" />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="form-date" />
              </div>
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger data-testid="form-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAYMENT_METHODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Person / Client / Team Member</Label>
              <Input value={form.person} onChange={(e) => setForm({ ...form, person: e.target.value })} required data-testid="form-person" />
            </div>
            <div className="space-y-2">
              <Label>Work / Purpose</Label>
              <Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="form-purpose" />
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="form-note" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="form-save">
                {saving ? "Saving..." : editing ? "Save" : "Add"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
